// [template-variables] Review the VARIABLES found in a template — the blanks and
// per-deal defaults a draft fills in — before they become the template's
// approved set. Deliberately never called "tags": the Edit tags modal edits
// TAXONOMY (deal type, guild, tier, CBA, agreement form), a different thing.
//
// Flow: Find variables (a 1-2 minute run, polled) → rename / Accept / Remove
// each → Save → Approve (template:certify only). A set whose template text has
// changed since it was found is stale: it can only be re-run.
import { useEffect, useMemo, useState } from "react";
import { Alert, Button, LoadingState } from "../index";
import { getApiErrorMessage } from "../../data/api";
import type { CertifiedTemplateRow } from "../../data/templatesQuery";
import {
  isFindingVariables,
  splitVariableContext,
  toVariableName,
  useFindTemplateVariables,
  useApproveTemplateVariables,
  useSaveTemplateVariables,
  useTemplateVariableProposal,
  useTemplateVariablesApproved,
  type TemplateRef,
  type TemplateVariable,
  type VariableDecision,
} from "../../data/templateVariablesQuery";

/** Past this, a run that never reported back is treated as lost, not running. */
const RUN_TIMEOUT_MS = 10 * 60 * 1000;

const REJECTED_REASON: Record<string, string> = {
  empty: "empty quote",
  touches_a_tag: "touches a variable already found",
  not_found: "text not found in the template",
  ambiguous: "appears more than once",
  collides: "overlaps a variable already found",
};

type Draft = { id: string; reviewed: boolean | null; sameAs: string | null };
const spanKey = (s: { start: number; end: number }): string => `${s.start}:${s.end}`;

export type TemplateVariablesReviewProps = {
  template: CertifiedTemplateRow;
  /** Session holds template:certify for this template's layer. */
  canApprove: boolean;
};

export function TemplateVariablesReview({
  template,
  canApprove,
}: TemplateVariablesReviewProps): JSX.Element {
  const ref: TemplateRef = useMemo(
    () => ({ templateId: template.id, tenantId: template.tenantId }),
    [template.id, template.tenantId]
  );
  const [startedAt, setStartedAt] = useState<string | null>(null);
  const [timedOut, setTimedOut] = useState(false);
  const proposalQuery = useTemplateVariableProposal(ref, startedAt);
  const approvedQuery = useTemplateVariablesApproved(ref);
  const find = useFindTemplateVariables(ref);
  const save = useSaveTemplateVariables(ref);
  const approve = useApproveTemplateVariables(ref);

  const job = proposalQuery.data?.job ?? null;
  const proposal = proposalQuery.data?.proposal ?? null;
  const finding = (find.isPending || isFindingVariables(proposalQuery.data, startedAt)) && !timedOut;

  // A run that started in an earlier visit is still reported by the server.
  useEffect(() => {
    if (job?.status === "running" && startedAt === null) setStartedAt(job.startedAt);
  }, [job, startedAt]);

  // Once the run is over (result stored, or failed), stop tracking it.
  useEffect(() => {
    if (startedAt !== null && !isFindingVariables(proposalQuery.data, startedAt)) {
      setStartedAt(null);
    }
  }, [proposalQuery.data, startedAt]);

  useEffect(() => {
    if (startedAt === null) return;
    const remaining = RUN_TIMEOUT_MS - (Date.now() - Date.parse(startedAt));
    const t = setTimeout(() => {
      setTimedOut(true);
      setStartedAt(null);
    }, Math.max(0, remaining));
    return () => clearTimeout(t);
  }, [startedAt]);

  // Local edits, reset whenever the stored proposal changes. React Query's
  // structural sharing keeps `proposal` referentially stable across identical
  // polls, so this does not wipe edits on a no-op refetch.
  const [drafts, setDrafts] = useState<Map<string, Draft>>(new Map());
  useEffect(() => {
    setDrafts(
      new Map(
        (proposal?.slots ?? []).map((s) => [
          spanKey(s),
          { id: s.id, reviewed: s.reviewed, sameAs: s.sameAs ?? null },
        ])
      )
    );
  }, [proposal]);

  const [showSkipped, setShowSkipped] = useState(false);
  // [S-802] Rows that still need attention are called out on the row itself, and
  // Save scrolls to the first — Diego accepted slots the finder could not name
  // and nothing said they would never be filled.

  const slots = proposal?.slots ?? [];
  const rows = slots.map((s) => ({ slot: s, draft: drafts.get(spanKey(s)) ?? s }));
  // [S-802] Counted over the rows that DEFINE a variable: an occurrence that
  // follows one repeats its name on purpose, and is not a clash.
  const nameCounts = new Map<string, number>();
  for (const r of rows) {
    if ((r.draft.sameAs ?? null) !== null) continue;
    const n = toVariableName(r.draft.id);
    nameCounts.set(n, (nameCounts.get(n) ?? 0) + 1);
  }
  /** A slot the finder could not name. Accepting one keeps a variable nothing
   *  can fill: values are matched to a variable BY NAME, so `unnamed_12` stays
   *  blank in every draft. Fine removed; otherwise it is the reviewer's to name
   *  or remove, so it is called out from the start, not only once accepted
   *  (Diego, 2026-10-16: the rows in red should be the issues, nothing else). */
  const unnamed = (d: { id: string; sameAs?: string | null }): boolean =>
    (d.sameAs ?? null) === null && /^unnamed_/.test(toVariableName(d.id));
  const needsName = (d: { id: string; reviewed: boolean | null; sameAs?: string | null }): boolean =>
    d.reviewed !== false && unnamed(d);
  const unnamedAccepted = rows.filter((r) => r.draft.reviewed === true && unnamed(r.draft)).length;
  const unnamedOpen = rows.filter((r) => needsName(r.draft)).length;

  const nameError = (raw: string): string | null => {
    const n = toVariableName(raw);
    if (n.length === 0) return "Needs a name";
    if ((nameCounts.get(n) ?? 0) > 1) return `Another variable is named ${n}`;
    return null;
  };
  const hasNameErrors = rows.some((r) => (r.draft.sameAs ?? null) === null && nameError(r.draft.id) !== null);
  const dirty = rows.some(
    (r) =>
      toVariableName(r.draft.id) !== r.slot.id ||
      r.draft.reviewed !== r.slot.reviewed ||
      (r.draft.sameAs ?? null) !== (r.slot.sameAs ?? null)
  );

  // [S-802] The variables an occurrence can point AT: the ones that hold a value
  // (not removed, not themselves a reference). A company is written in many
  // places — the first occurrence names it and the rest take its value, so one
  // value fills them all and the names stay unique.
  const referenceable = (self: TemplateVariable): string[] =>
    rows
      .filter(
        (r) =>
          spanKey(r.slot) !== spanKey(self) &&
          r.draft.reviewed !== false &&
          r.draft.sameAs === null &&
          toVariableName(r.draft.id).length > 0
      )
      .map((r) => toVariableName(r.draft.id))
      .filter((n, i, all) => all.indexOf(n) === i)
      .sort();
  /** [S-802] The proposer names a second occurrence `company_2`, which usually
   *  means it IS the company again — but not always: on a performer agreement
   *  `tel`/`tel_2` are the Company's number and the Artist's. So this only
   *  offers the link; the reviewer is the one who knows. */
  const repeatOf = (self: TemplateVariable, draft: { id: string; sameAs?: string | null }): string | null => {
    if ((draft.sameAs ?? null) !== null) return null;
    const base = /^(.+)_\d+$/.exec(toVariableName(draft.id))?.[1] ?? null;
    return base !== null && referenceable(self).includes(base) ? base : null;
  };
  const accepted = rows.filter((r) => r.draft.reviewed === true).length;
  const removed = rows.filter((r) => r.draft.reviewed === false).length;
  const undecided = rows.length - accepted - removed;
  // [2026-10-16] Accept all takes the rule-found rows that have a name. An
  // unnamed one is a decision only the reviewer can make (name it or remove
  // it); accepting it in bulk turned it red and it had to be undone by hand.
  const ruleAcceptable = (s: TemplateVariable, d: { id: string; reviewed: boolean | null; sameAs?: string | null }): boolean =>
    s.phase === "deterministic" && d.reviewed !== true && !unnamed(d);
  const ruleUndecided = rows.filter((r) => ruleAcceptable(r.slot, r.draft)).length;
  // [2026-10-16] Diego: after a quick read, accept every named variable at
  // once — found by rule or by model. Same exception: an unnamed one stays red.
  const acceptable = (d: { id: string; reviewed: boolean | null; sameAs?: string | null }): boolean =>
    d.reviewed === null && !unnamed(d);
  const allUndecided = rows.filter((r) => acceptable(r.draft)).length;
  const stale = proposal?.stale === true;
  const locked = finding || stale || save.isPending || approve.isPending;

  const setDraft = (s: TemplateVariable, patch: Partial<Draft>): void => {
    setDrafts((prev) => {
      const next = new Map(prev);
      const cur = prev.get(spanKey(s)) ?? { id: s.id, reviewed: s.reviewed, sameAs: s.sameAs ?? null };
      next.set(spanKey(s), { ...cur, ...patch });
      return next;
    });
  };

  const acceptAllRuleFound = (): void => {
    setDrafts((prev) => {
      const next = new Map(prev);
      for (const s of slots) {
        const cur = prev.get(spanKey(s)) ?? { id: s.id, reviewed: s.reviewed, sameAs: s.sameAs ?? null };
        if (!ruleAcceptable(s, cur)) continue;
        next.set(spanKey(s), { ...cur, reviewed: true });
      }
      return next;
    });
  };

  const acceptAll = (): void => {
    setDrafts((prev) => {
      const next = new Map(prev);
      for (const s of slots) {
        const cur = prev.get(spanKey(s)) ?? { id: s.id, reviewed: s.reviewed, sameAs: s.sameAs ?? null };
        if (acceptable(cur)) next.set(spanKey(s), { ...cur, reviewed: true });
      }
      return next;
    });
  };

  const handleFind = (): void => {
    setTimedOut(false);
    save.reset();
    approve.reset();
    find.mutate(undefined, { onSuccess: (res) => setStartedAt(res.job.startedAt) });
  };

  const handleSave = (): void => {
    // [S-802] Only what this reviewer actually changed. Sending every row made
    // a Save a full overwrite of the stored proposal in the values this tab
    // happened to load, so a second tab -- or the same tab left open across
    // someone else's save -- silently reverted decisions nobody revisited
    // (37 accepts, live). A row left alone here keeps whatever the store holds.
    const decisions: VariableDecision[] = rows
      .filter(
        (r) =>
          r.draft.id !== r.slot.id ||
          r.draft.reviewed !== r.slot.reviewed ||
          (r.draft.sameAs ?? null) !== (r.slot.sameAs ?? null)
      )
      .map((r) => ({
        start: r.slot.start,
        end: r.slot.end,
        id: r.draft.id,
        reviewed: r.draft.reviewed,
        sameAs: r.draft.sameAs,
      }));
    approve.reset();
    save.mutate(decisions);
    // Put the first one in front of the reviewer rather than leaving it to be
    // found in a 98-row list.
    if (unnamedAccepted > 0) {
      window.setTimeout(() => {
        document.querySelector("[data-testid=tv-needs-name]")?.scrollIntoView({ block: "center" });
      }, 0);
    }
  };

  const approved = approvedQuery.data?.approved ?? null;
  const findError = find.isError ? getApiErrorMessage(find.error) ?? find.error.message : null;
  const saveError = save.isError ? getApiErrorMessage(save.error) ?? save.error.message : null;
  const approveError = approve.isError
    ? getApiErrorMessage(approve.error) ?? approve.error.message
    : null;

  return (
    <div className="tv" data-testid="tv-review">
      <div className="tv__bar">
        <div className="tv__status" data-testid="tv-approved-status">
          {approvedQuery.isLoading
            ? "Checking approved variables…"
            : approved === null
              ? "No variables approved for this template yet."
              : `Approved: ${approved.slots.length} variables · ${formatDateTime(approved.updatedAt)}${
                  approved.stale ? " · template changed since — re-run and approve again" : ""
                }`}
        </div>
        <Button
          variant={proposal === null ? "primary" : "secondary"}
          onClick={handleFind}
          loading={finding}
          disabled={finding}
          data-testid="tv-find"
        >
          {finding ? "Finding variables…" : proposal === null ? "Find variables" : "Find variables again"}
        </Button>
      </div>

      {finding && (
        <LoadingState label="Finding variables in the template. This takes 1–2 minutes; you can leave this page and come back." />
      )}
      {timedOut && (
        <Alert label="No result yet" tone="error" data-testid="tv-timeout">
          The run didn&apos;t report back within 10 minutes. Find variables again.
        </Alert>
      )}
      {job?.status === "failed" && (
        <Alert label="Finding variables failed" tone="error" data-testid="tv-failed">
          {job.error ?? "Unknown error."} Find variables again.
        </Alert>
      )}
      {findError !== null && (
        <Alert label="Couldn't start" tone="error">
          {findError}
        </Alert>
      )}
      {proposalQuery.isError && (
        <Alert label="Couldn't load variables" tone="error">
          {getApiErrorMessage(proposalQuery.error) ?? proposalQuery.error.message}
        </Alert>
      )}

      {proposalQuery.isLoading && <LoadingState label="Loading variables…" />}

      {!proposalQuery.isLoading && proposal === null && !finding && job?.status !== "failed" && (
        <div className="tv__empty" data-testid="tv-empty">
          You haven&apos;t found variables for this template yet. Find variables to
          list every blank and per-deal value, then accept or remove each one.
        </div>
      )}

      {proposal !== null && (
        <>
          {stale && (
            <Alert label="Template changed" tone="error" data-testid="tv-stale">
              The template&apos;s text changed after these variables were found, so their
              positions may now point at the wrong words. Find variables again — this
              set can&apos;t be saved or approved.
            </Alert>
          )}

          <div className="tv__bar">
            <div className="tv__counts" data-testid="tv-counts">
              {rows.length} variables · {accepted} accepted · {removed} removed · {undecided}{" "}
              undecided
              {unnamedOpen > 0 && (
                <span className="tv__needs-name-count" data-testid="tv-needs-name-count">
                  {` · ${String(unnamedOpen)} need a name`}
                </span>
              )}
            </div>
            <div className="tv__actions">
              <Button
                variant="secondary"
                size="s"
                onClick={acceptAllRuleFound}
                disabled={locked || ruleUndecided === 0}
                data-testid="tv-accept-rule"
              >
                Accept all rule-found ({ruleUndecided})
              </Button>
              <Button
                variant="secondary"
                size="s"
                onClick={acceptAll}
                disabled={locked || allUndecided === 0}
                title="Accepts every undecided variable that has a name. Unnamed ones stay for you to name or remove."
                data-testid="tv-accept-all"
              >
                Accept all ({allUndecided})
              </Button>
              <Button
                variant="secondary"
                size="s"
                onClick={handleSave}
                loading={save.isPending}
                disabled={locked || !dirty || hasNameErrors}
                data-testid="tv-save"
              >
                Save
              </Button>
              {canApprove && (
                <Button
                  variant="primary"
                  size="s"
                  onClick={() => approve.mutate()}
                  loading={approve.isPending}
                  disabled={locked || dirty || undecided > 0 || unnamedAccepted > 0}
                  title={
                    dirty
                      ? "Save your changes first"
                      : undecided > 0
                        ? "Accept or remove every variable first"
                        : unnamedAccepted > 0
                          ? `Name the ${String(unnamedAccepted)} accepted variables the finder couldn’t name, or remove them`
                        : "Publish these variables: every new draft from this template fills them"
                  }
                  data-testid="tv-approve"
                >
                  {/* Support ticket #310: "Save" then no way to publish — the step is this button. */}
                  Approve &amp; publish
                </Button>
              )}
            </div>
          </div>
          {save.isSuccess && !dirty && !approve.isSuccess && (
            <Alert label="Saved" data-testid="tv-saved-next">
              {canApprove
                ? "Your decisions are saved. Approve & publish to use these variables in drafts."
                : "Your decisions are saved. Someone with approval rights needs to approve & publish them before drafts use them."}
            </Alert>
          )}
          {saveError !== null && (
            <div className="td__attest__err" role="alert">
              Save failed — {saveError}
            </div>
          )}
          {approveError !== null && (
            <div className="td__attest__err" role="alert">
              Approve failed — {approveError}
            </div>
          )}
          {approve.isSuccess && (
            <div className="tv__ok" role="status" data-testid="tv-approved">
              Approved {approve.data.approved.slots.length} variables.
            </div>
          )}

          <ul className="tv__list" data-testid="tv-list">
            {rows.map(({ slot, draft }) => {
              const err = nameError(draft.id);
              const parts = splitVariableContext(slot);
              const state =
                draft.reviewed === true ? "accepted" : draft.reviewed === false ? "removed" : "open";
              return (
                <li
                  key={spanKey(slot)}
                  className={`tv__row tv__row--${state}${needsName(draft) ? " tv__row--needs-name" : ""}`}
                  data-testid="tv-row"
                >
                  <div className="tv__row__head">
                    <input
                      type="text"
                      className="tv__name"
                      value={draft.id}
                      readOnly={draft.sameAs !== null}
                      title={draft.sameAs !== null ? `Another occurrence of “${draft.sameAs}” — rename the variable itself to change this` : undefined}
                      aria-label={`Variable name for “${slot.current.slice(0, 40)}”`}
                      onChange={(e) => setDraft(slot, { id: e.target.value })}
                      onBlur={() => {
                        const n = toVariableName(draft.id);
                        if (n.length > 0 && n !== draft.id) setDraft(slot, { id: n });
                      }}
                      disabled={locked}
                    />
                    <label className="tv__sameas">
                      <span className="tv__sameas__label">Same as</span>
                      <select
                        className="tv__sameas__select"
                        aria-label={`Take the value of another variable for “${slot.current.slice(0, 40)}”`}
                        value={draft.sameAs ?? ""}
                        onChange={(e) => {
                          // [S-802] An occurrence IS the variable it follows, so it
                          // takes that name — one variable, written in several places,
                          // not `company` and `company_2`.
                          const to = e.target.value;
                          setDraft(slot, to.length === 0 ? { sameAs: null } : { sameAs: to, id: to });
                        }}
                        disabled={locked}
                        data-testid="tv-sameas"
                      >
                        <option value="">its own value</option>
                        {referenceable(slot).map((n) => (
                          <option key={n} value={n}>
                            {n}
                          </option>
                        ))}
                      </select>
                    </label>
                    {repeatOf(slot, draft) !== null && (
                      <Button
                        variant="ghost"
                        size="xs"
                        disabled={locked}
                        data-testid="tv-repeat-hint"
                        onClick={() => {
                          const base = repeatOf(slot, draft);
                          if (base !== null) setDraft(slot, { sameAs: base, id: base });
                        }}
                      >
                        Same as {repeatOf(slot, draft)}?
                      </Button>
                    )}
                    <span
                      className={`tv__phase tv__phase--${slot.phase}`}
                      title={slot.rule}
                    >
                      {slot.phase === "deterministic" ? "Found by rule" : "Found by model"}
                    </span>
                    <span className="tv__rule">{slot.rule}</span>
                    <div className="tv__row__actions">
                      <Button
                        variant={draft.reviewed === true ? "primary" : "secondary"}
                        size="xs"
                        aria-pressed={draft.reviewed === true}
                        onClick={() =>
                          setDraft(slot, { reviewed: draft.reviewed === true ? null : true })
                        }
                        disabled={locked}
                      >
                        Accept
                      </Button>
                      <Button
                        variant={draft.reviewed === false ? "danger" : "ghost"}
                        size="xs"
                        aria-pressed={draft.reviewed === false}
                        onClick={() =>
                          setDraft(slot, { reviewed: draft.reviewed === false ? null : false })
                        }
                        disabled={locked}
                      >
                        Remove
                      </Button>
                    </div>
                  </div>
                  {err !== null && (
                    <div className="tv__name-err" role="alert">
                      {err}
                    </div>
                  )}
                  {err === null && needsName(draft) && (
                    <div className="tv__name-err" role="alert" data-testid="tv-needs-name">
                      Give this one a name — the finder couldn’t, and a variable is
                      filled by name, so “{toVariableName(draft.id)}” would stay blank in every
                      draft. Remove it instead to keep the template’s own wording.
                    </div>
                  )}
                  <div className="tv__current">
                    Current text: <code>{slot.current.length > 0 ? slot.current : "(empty)"}</code>
                  </div>
                  <div className="tv__context">
                    {parts === null ? (
                      slot.context
                    ) : (
                      <>
                        …{parts.before}
                        <mark className="tv__mark">{parts.match}</mark>
                        {parts.after}…
                      </>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>

          <div className="tv__skipped">
            <Button
              variant="bare"
              className="tv__skipped__head"
              onClick={() => setShowSkipped((v) => !v)}
              aria-expanded={showSkipped}
              data-testid="tv-skipped-toggle"
            >
              {showSkipped ? "▾" : "▸"} Not included: {proposal.rejected.length} skipped by the finder ·{" "}
              {proposal.editorialNotes.length} editorial notes, kept in the draft unless the deal resolves them
            </Button>
            {showSkipped && (
              <div className="tv__skipped__body">
                <div className="tv__skipped__lbl">Skipped by the finder</div>
                {proposal.rejected.length === 0 ? (
                  <div className="tv__skipped__none">None.</div>
                ) : (
                  <ul className="tv__skipped__list">
                    {proposal.rejected.map((r, i) => (
                      <li key={i}>
                        <code>{r.quote.length > 0 ? r.quote : "(empty)"}</code>{" "}
                        <span className="tv__rule">{REJECTED_REASON[r.reason] ?? r.reason}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="tv__skipped__lbl">Editorial notes (kept in the draft for the lawyer)</div>
                {proposal.editorialNotes.length === 0 ? (
                  <div className="tv__skipped__none">None.</div>
                ) : (
                  <ul className="tv__skipped__list">
                    {proposal.editorialNotes.map((n) => (
                      <li key={`${n.start}:${n.end}`}>
                        <code>{n.text}</code>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
